import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ContentErrorBoundaryFallbackProps {
  error?: Error | null;
}

export function ContentErrorBoundaryFallback({ error }: ContentErrorBoundaryFallbackProps) {
  const { t } = useTranslation(['common']);
  const [copied, setCopied] = useState(false);
  const [showStack, setShowStack] = useState(true);

  const handleCopyError = () => {
    const errorInfo = `URL: ${window.location.href}
Context: Page Content Error
Error: ${error?.message || 'N/A'}
Stack: ${error?.stack || 'N/A'}`;
    navigator.clipboard.writeText(errorInfo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy error info:', err);
    });
  };

  return (
    <div className="w-full flex flex-col items-center justify-center p-6 my-8 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <svg className="w-12 h-12 text-orange-500 dark:text-orange-400 mb-3" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h2 className="text-xl font-semibold text-orange-600 dark:text-orange-400 mb-2">
        {t('common:errors.contentLoadingError')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4 text-center px-4">
        {t('common:errors.contentLoadingErrorMessage')}
      </p>

      {error && (
        <div className="w-full max-w-lg text-left bg-gray-50 dark:bg-gray-700 p-3.5 rounded-md mb-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('common:errors.errorLabel')}: <span className="font-normal text-red-500 dark:text-red-400">{error.message || t('common:errors.noErrorMessage')}</span></p>
          {error.stack && (
            <div className="mt-2">
              <button
                onClick={() => setShowStack(!showStack)}
                className="text-xs text-blue-500 dark:text-blue-400 hover:underline mb-1"
              >
                {showStack ? t('common:actions.hide') : t('common:actions.show')} {t('common:errors.errorDetails')}
              </button>
              {showStack && (
                <pre className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 p-2 rounded overflow-auto max-h-28">
                  {error.stack}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-3 mt-2">
        <button
          className="px-5 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 dark:hover:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
          onClick={() => window.location.href = '/'}
        >
          {t('common:actions.returnToHomepage')}
        </button>
        <button
           className={`px-5 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors ${copied ? 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 focus:ring-gray-500'}`}
          onClick={handleCopyError}
        >
          {copied ? t('common:actions.copiedError') : t('common:actions.copyErrorInfo')}
        </button>
      </div>
    </div>
  );
}

export default ContentErrorBoundaryFallback;